import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Plus, Search, DollarSign, Calendar, Building2,
  UserCheck, Calculator, FileText, Download, MoreVertical,
  Banknote, Shield, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Payroll = () => {
  const { token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Employee Dialog
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    ic_number: '',
    department: '',
    position: '',
    hire_date: '',
    basic_salary: 0,
    bank_name: '',
    bank_account: '',
    epf_number: '',
    socso_number: '',
    tax_number: ''
  });

  // Payroll Dialog
  const [isPayrollDialogOpen, setIsPayrollDialogOpen] = useState(false);
  const [payrollForm, setPayrollForm] = useState({
    pay_period_start: '',
    pay_period_end: '',
    payment_date: '',
    items: []
  });

  useEffect(() => {
    if (!authLoading && token) {
      fetchEmployees();
      fetchPayrolls();
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [token, authLoading]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrolls = async () => {
    try {
      const response = await axios.get(`${API}/payroll`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayrolls(response.data);
    } catch (error) {
      console.error('Failed to fetch payrolls');
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employees`, employeeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee added successfully');
      setIsEmployeeDialogOpen(false);
      fetchEmployees();
      resetEmployeeForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding employee');
    }
  };

  const handleCreatePayroll = async (e) => {
    e.preventDefault();
    try {
      // Build payroll items from employees
      const items = employees.filter(emp => emp.is_active).map(emp => ({
        employee_id: emp.id,
        basic_salary: emp.basic_salary,
        allowances: 0,
        overtime: 0,
        deductions: 0
      }));

      await axios.post(`${API}/payroll`, { ...payrollForm, items }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payroll created successfully');
      setIsPayrollDialogOpen(false);
      fetchPayrolls();
      resetPayrollForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating payroll');
    }
  };

  const handleApprovePayroll = async (payrollId) => {
    try {
      await axios.patch(`${API}/payroll/${payrollId}/status?status=approved`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payroll approved');
      fetchPayrolls();
    } catch (error) {
      toast.error('Error approving payroll');
    }
  };

  const handleProcessPayroll = async (payrollId) => {
    try {
      await axios.patch(`${API}/payroll/${payrollId}/status?status=paid`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payroll processed and marked as paid');
      fetchPayrolls();
    } catch (error) {
      toast.error('Error processing payroll');
    }
  };

  const downloadPayrollPDF = (payrollId) => {
    window.open(`${API_URL}/api/payroll/${payrollId}/pdf`, '_blank');
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      full_name: '',
      email: '',
      phone: '',
      ic_number: '',
      department: '',
      position: '',
      hire_date: '',
      basic_salary: 0,
      bank_name: '',
      bank_account: '',
      epf_number: '',
      socso_number: '',
      tax_number: ''
    });
  };

  const resetPayrollForm = () => {
    setPayrollForm({
      pay_period_start: '',
      pay_period_end: '',
      payment_date: '',
      items: []
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500/20 text-emerald-400';
      case 'approved': return 'bg-blue-500/20 text-blue-400';
      case 'processing': return 'bg-amber-500/20 text-amber-400';
      case 'draft': return 'bg-slate-500/20 text-slate-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const totalMonthlyPayroll = employees.reduce((sum, e) => sum + (e.is_active ? e.basic_salary : 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Payroll</h1>
          <p className="text-slate-400">Manage employees and process payroll</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Users className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Employees</p>
                <p className="text-xl font-bold text-white">{employees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <UserCheck className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Active</p>
                <p className="text-xl font-bold text-white">
                  {employees.filter(e => e.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Monthly Payroll</p>
                <p className="text-xl font-bold text-white">
                  MYR {totalMonthlyPayroll.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Payroll Runs</p>
                <p className="text-xl font-bold text-white">{payrolls.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border-slate-700">
          <TabsTrigger value="employees" className="data-[state=active]:bg-indigo-600">
            Employees
          </TabsTrigger>
          <TabsTrigger value="payrolls" className="data-[state=active]:bg-indigo-600">
            Payroll Runs
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            <Dialog open={isEmployeeDialogOpen} onOpenChange={setIsEmployeeDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">Add New Employee</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateEmployee} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-slate-300">Full Name *</Label>
                      <Input
                        value={employeeForm.full_name}
                        onChange={(e) => setEmployeeForm({...employeeForm, full_name: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Email *</Label>
                      <Input
                        type="email"
                        value={employeeForm.email}
                        onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Phone</Label>
                      <Input
                        value={employeeForm.phone}
                        onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">IC Number *</Label>
                      <Input
                        value={employeeForm.ic_number}
                        onChange={(e) => setEmployeeForm({...employeeForm, ic_number: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        placeholder="e.g., 901234-12-5678"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Position *</Label>
                      <Input
                        value={employeeForm.position}
                        onChange={(e) => setEmployeeForm({...employeeForm, position: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Department</Label>
                      <Input
                        value={employeeForm.department}
                        onChange={(e) => setEmployeeForm({...employeeForm, department: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Hire Date *</Label>
                      <Input
                        type="date"
                        value={employeeForm.hire_date}
                        onChange={(e) => setEmployeeForm({...employeeForm, hire_date: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-slate-300">Basic Salary (MYR) *</Label>
                      <Input
                        type="number"
                        value={employeeForm.basic_salary}
                        onChange={(e) => setEmployeeForm({...employeeForm, basic_salary: parseFloat(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Bank Name</Label>
                      <Input
                        value={employeeForm.bank_name}
                        onChange={(e) => setEmployeeForm({...employeeForm, bank_name: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Bank Account</Label>
                      <Input
                        value={employeeForm.bank_account}
                        onChange={(e) => setEmployeeForm({...employeeForm, bank_account: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">EPF Number</Label>
                      <Input
                        value={employeeForm.epf_number}
                        onChange={(e) => setEmployeeForm({...employeeForm, epf_number: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">SOCSO Number</Label>
                      <Input
                        value={employeeForm.socso_number}
                        onChange={(e) => setEmployeeForm({...employeeForm, socso_number: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-slate-300">Tax Number (LHDN)</Label>
                      <Input
                        value={employeeForm.tax_number}
                        onChange={(e) => setEmployeeForm({...employeeForm, tax_number: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsEmployeeDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      Add Employee
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Employee List */}
          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : employees.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-10 text-center">
                <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No employees found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {employees
                .filter(e => e.full_name?.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((employee) => (
                <motion.div
                  key={employee.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                            <Users className="w-6 h-6 text-indigo-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-white">{employee.full_name}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${employee.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                {employee.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3 h-3" />
                                {employee.position}
                              </span>
                              {employee.department && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {employee.department}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Joined: {employee.hire_date}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-white">
                            MYR {employee.basic_salary?.toLocaleString()}
                          </p>
                          <p className="text-sm text-slate-400">Basic Salary</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Payrolls Tab */}
        <TabsContent value="payrolls" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isPayrollDialogOpen} onOpenChange={setIsPayrollDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={employees.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Run Payroll
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Create Payroll Run</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreatePayroll} className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Pay Period Start *</Label>
                    <Input
                      type="date"
                      value={payrollForm.pay_period_start}
                      onChange={(e) => setPayrollForm({...payrollForm, pay_period_start: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Pay Period End *</Label>
                    <Input
                      type="date"
                      value={payrollForm.pay_period_end}
                      onChange={(e) => setPayrollForm({...payrollForm, pay_period_end: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Payment Date *</Label>
                    <Input
                      type="date"
                      value={payrollForm.payment_date}
                      onChange={(e) => setPayrollForm({...payrollForm, payment_date: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-slate-300 text-sm">
                      This will create payroll for <strong>{employees.filter(e => e.is_active).length}</strong> active employees
                    </p>
                    <p className="text-slate-400 text-sm mt-1">
                      Estimated Gross: MYR {totalMonthlyPayroll.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      EPF, SOCSO, EIS, and PCB will be calculated automatically based on Malaysian regulations.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsPayrollDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      Create Payroll
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Payroll List */}
          {payrolls.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-10 text-center">
                <Calculator className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No payroll runs found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {payrolls.map((payroll) => (
                <motion.div
                  key={payroll.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                            <Calculator className="w-6 h-6 text-purple-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-white">{payroll.payroll_number}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(payroll.status)}`}>
                                {payroll.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                              <span>Period: {payroll.pay_period_start} to {payroll.pay_period_end}</span>
                              <span>Payment: {payroll.payment_date}</span>
                              <span>{payroll.items?.length} employees</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-sm text-slate-400">Gross</p>
                            <p className="font-semibold text-white">MYR {payroll.total_gross?.toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-400">Net</p>
                            <p className="font-semibold text-emerald-400">MYR {payroll.total_net?.toLocaleString()}</p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4 text-slate-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-slate-800 border-slate-700">
                              {payroll.status === 'draft' && (
                                <DropdownMenuItem 
                                  className="text-slate-300"
                                  onClick={() => handleApprovePayroll(payroll.id)}
                                >
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                              )}
                              {payroll.status === 'approved' && (
                                <DropdownMenuItem 
                                  className="text-slate-300"
                                  onClick={() => handleProcessPayroll(payroll.id)}
                                >
                                  <Banknote className="w-4 h-4 mr-2" />
                                  Process & Pay
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                className="text-slate-300"
                                onClick={() => downloadPayrollPDF(payroll.id)}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download PDF
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      
                      {/* Deductions Summary */}
                      <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-4 gap-4">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">EPF (Employee)</p>
                          <p className="text-sm text-slate-300">MYR {payroll.total_epf_employee?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">SOCSO (Employee)</p>
                          <p className="text-sm text-slate-300">MYR {payroll.total_socso_employee?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">EIS (Employee)</p>
                          <p className="text-sm text-slate-300">MYR {payroll.total_eis_employee?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">PCB (Tax)</p>
                          <p className="text-sm text-slate-300">MYR {payroll.total_pcb?.toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Payroll;
